import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { InstagramIcon } from "@/components/InstagramIcon";
import { Loader2 } from "lucide-react";

type EngagedFansNdjsonEvent =
  | { type: "init"; phase: "media" | "comments" | "leads"; totalMedia?: number; total?: number }
  | { type: "media-progress"; fetched: number; batchSize: number }
  | { type: "media-complete"; totalMedia: number }
  | {
      type: "comments-progress";
      mediaIndex: number;
      totalMedia: number;
      mediaId: string;
      commentsOnMedia: number;
      totalComments: number;
      uniqueCommenters: number;
    }
  | {
      type: "comments-error";
      mediaId: string;
      mediaIndex: number;
      totalMedia: number;
      message: string;
    }
  | { type: "comments-complete"; totalComments: number; uniqueCommenters: number }
  | {
      type: "lead";
      index: number;
      total: number;
      lead: {
        _id: string;
        igUsername?: string;
        numEngagements?: number;
        joinedAt?: string;
        lastActive?: string;
      };
    }
  | {
      type: "complete";
      totalMedia: number;
      totalComments: number;
      uniqueCommenters: number;
      totalLeads: number;
    }
  | { type: "error"; message: string };

type FindProgress = {
  phase: "media" | "comments" | "leads";
  mediaFetched?: number;
  totalMedia?: number;
  mediaIndex?: number;
  totalComments?: number;
  uniqueCommenters?: number;
  leadIndex?: number;
  totalLeads?: number;
};

async function consumeEngagedFansNdjsonStream({
  response,
  onEvent,
}: {
  response: Response;
  onEvent: (event: EngagedFansNdjsonEvent) => void;
}): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      onEvent(JSON.parse(trimmed) as EngagedFansNdjsonEvent);
    }
  }
  const tail = buffer.trim();
  if (tail) {
    onEvent(JSON.parse(tail) as EngagedFansNdjsonEvent);
  }
}

function handleSyncEvent({
  event,
  setProgress,
  setEngagedFanCount,
  setSyncCompleted,
  setStatusMessage,
}: {
  event: EngagedFansNdjsonEvent;
  setProgress: Dispatch<SetStateAction<FindProgress | null>>;
  setEngagedFanCount: Dispatch<SetStateAction<number>>;
  setSyncCompleted: Dispatch<SetStateAction<boolean>>;
  setStatusMessage: Dispatch<SetStateAction<string | null>>;
}) {
  if (event.type === "error") {
    throw new Error(event.message);
  }
  if (event.type === "init") {
    setProgress((prev) => ({
      phase: event.phase,
      mediaFetched: prev?.mediaFetched,
      totalMedia: event.totalMedia ?? prev?.totalMedia,
      totalLeads: event.total ?? prev?.totalLeads,
    }));
  }
  if (event.type === "media-progress") {
    setProgress((prev) => ({
      phase: "media",
      mediaFetched: event.fetched,
      totalMedia: prev?.totalMedia,
    }));
  }
  if (event.type === "media-complete") {
    setProgress({
      phase: "comments",
      totalMedia: event.totalMedia,
      mediaIndex: 0,
      totalComments: 0,
      uniqueCommenters: 0,
    });
  }
  if (event.type === "comments-progress") {
    setProgress({
      phase: "comments",
      totalMedia: event.totalMedia,
      mediaIndex: event.mediaIndex,
      totalComments: event.totalComments,
      uniqueCommenters: event.uniqueCommenters,
    });
  }
  if (event.type === "comments-complete") {
    setProgress({
      phase: "leads",
      totalComments: event.totalComments,
      uniqueCommenters: event.uniqueCommenters,
      leadIndex: 0,
      totalLeads: event.uniqueCommenters,
    });
  }
  if (event.type === "lead") {
    setProgress({
      phase: "leads",
      leadIndex: event.index,
      totalLeads: event.total,
      uniqueCommenters: event.total,
    });
  }
  if (event.type === "complete") {
    setEngagedFanCount(event.totalLeads);
    setSyncCompleted(true);
    setStatusMessage(
      `Found ${event.uniqueCommenters} engaged fans from ${event.totalComments} comments on ${event.totalMedia} posts`,
    );
  }
}

export function InstagramFansRoute() {
  const [engagedFanCount, setEngagedFanCount] = useState(0);
  const [isLoadingCount, setIsLoadingCount] = useState(true);
  const [isFinding, setIsFinding] = useState(true);
  const [syncCompleted, setSyncCompleted] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusError, setStatusError] = useState(false);
  const [progress, setProgress] = useState<FindProgress | null>({
    phase: "media",
    mediaFetched: 0,
  });

  useEffect(() => {
    let cancelled = false;

    async function fetchCount() {
      setIsLoadingCount(true);
      try {
        const response = await fetch("/api/instagram/engaged-fans");
        if (response.ok) {
          const data = (await response.json()) as { count: number };
          if (!cancelled) {
            setEngagedFanCount(data.count);
          }
        }
      } catch (error) {
        console.error("Failed to fetch engaged fan count:", error);
      } finally {
        if (!cancelled) {
          setIsLoadingCount(false);
        }
      }
    }

    async function streamSyncStatus() {
      setIsFinding(true);
      setSyncCompleted(false);
      setStatusMessage(null);
      setStatusError(false);
      setProgress({ phase: "media", mediaFetched: 0 });

      try {
        const response = await fetch("/api/instagram/engaged-fans/sync");

        if (!response.ok) {
          const data = (await response.json()) as { error?: string };
          throw new Error(data.error || "Failed to stream sync status");
        }

        await consumeEngagedFansNdjsonStream({
          response,
          onEvent: (event) => {
            if (cancelled) return;
            handleSyncEvent({
              event,
              setProgress,
              setEngagedFanCount,
              setSyncCompleted,
              setStatusMessage,
            });
          },
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Stream sync status error:", error);
          setStatusError(true);
          setStatusMessage(
            error instanceof Error ? error.message : "Failed to stream sync status",
          );
        }
      } finally {
        if (!cancelled) {
          setIsFinding(false);
          setProgress(null);
        }
      }
    }

    fetchCount();
    streamSyncStatus();

    return () => {
      cancelled = true;
    };
  }, []);

  const progressLabel = (() => {
    if (!progress) return null;
    if (progress.phase === "media") {
      return `Fetching posts… ${progress.mediaFetched ?? 0} loaded`;
    }
    if (progress.phase === "comments" && progress.totalMedia) {
      return `Scanning comments… post ${progress.mediaIndex ?? 0} of ${progress.totalMedia} (${progress.uniqueCommenters ?? 0} fans found)`;
    }
    if (progress.phase === "leads" && progress.totalLeads) {
      return `Saving fans… ${progress.leadIndex ?? 0} of ${progress.totalLeads}`;
    }
    return null;
  })();

  return (
    <div className="container mx-auto flex min-h-screen flex-col items-center p-8">
      <div className="flex w-full flex-1 flex-col items-center justify-center">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-pink-50">
            <InstagramIcon className="h-8 w-8 text-pink-600" />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            {isLoadingCount ? (
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
            ) : (
              <p className="text-6xl font-bold">{engagedFanCount}</p>
            )}
            <p className="text-muted-foreground mt-2 text-sm">fans</p>
            {isFinding ? (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {progressLabel ?? "Syncing engaged fans…"}
              </div>
            ) : null}
            {statusMessage ? (
              <p
                className={`mt-4 text-sm ${statusError ? "text-destructive" : "text-muted-foreground"}`}
              >
                {statusMessage}
              </p>
            ) : null}
          </div>
          <div className="space-y-3">
            {syncCompleted && engagedFanCount > 0 ? (
              <Button variant="outline" className="w-full" size="lg" asChild>
                <Link to="/instagram/fans/list">See {engagedFanCount} Fans</Link>
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
      </div>
      <footer className="flex flex-col items-center gap-1 text-center">
        <p className="text-xs text-muted-foreground">Engram (formerly Kaizen Place)</p>
        <p className="text-xs text-muted-foreground">
          <a
            href="mailto:hello@engram.sh"
            className="font-medium text-primary underline underline-offset-4"
          >
            Contact Us
          </a>
        </p>
      </footer>
    </div>
  );
}
